/**
 * DashboardPage Component
 *
 * How the ads are doing across every connected platform: spend, results and
 * cost per result for the period, a daily chart split by platform, what is
 * working, one row per platform (click to filter the page to it) and the
 * campaigns behind the numbers.
 *
 * Usage:
 *   <Route path="/dashboard" element={<DashboardPage />} />
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDesk } from '@/components/growth-desk/data/DeskData';
import { monthLabel, money, shortDate } from '@/components/growth-desk/data/status';
import { DESK } from '@/components/growth-desk/links';
import { DeskButton, Notice, Segmented } from '@/components/growth-desk/ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';
import { MetricCard } from '@/components/dashboard/MetricCard';
import { DailyChart } from '@/components/dashboard/DailyChart';
import { PlatformTable, SyncLine } from '@/components/dashboard/PlatformTable';
import { CampaignsTable, type CampaignSort } from '@/components/dashboard/CampaignsTable';
import { HighlightsCard } from '@/components/dashboard/HighlightsCard';
import { LeadsCard } from '@/components/dashboard/LeadsCard';
import { leadsApi } from '@/services/leadsApi';
import type { Lead, LeadsSummary } from '@/types/leads';
import { AnalyticsUpgradePrompt } from '@/components/dashboard/AnalyticsUpgradePrompt';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { apiClient } from '@/services/api';
import { analyticsApi } from '@/services/analyticsApi';
import type {
  AdPlatform,
  CampaignSummary,
  ChartData,
  ChartMetric,
  DashboardMetrics,
  Highlight,
  PlatformSyncStatus,
  TimeRange,
} from '@/types/analytics';
import { WelcomePathModal, CapabilityCard, CAPABILITY, INFO, isSeen, WELCOME_ID } from '@/components/onboarding';
import { useAuth } from '@/hooks/useAuth';
import { useAnalyticsCapabilities } from '@/hooks/useAnalyticsCapabilities';
import { useTheme } from '@/hooks/useTheme';
import { useToast } from '@/components/ui/Toast';
import {
  PLATFORM_COLOR,
  PLATFORM_LABEL,
  PLATFORM_ORDER,
  formatCount,
  formatMoney,
  formatPct,
  resultsBreakdown,
} from '@/utils/analyticsFormat';
import { PlanVerdictLine } from '@/components/growth-desk/plan/PlanVsActual';

const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === 'true';

const RANGE_DAYS: Record<TimeRange, number> = { '7d': 7, '30d': 30, '90d': 90 };

const CHART_METRICS: { value: ChartMetric; label: string; title: string }[] = [
  { value: 'spend', label: 'Spend', title: 'Daily spend' },
  { value: 'results', label: 'Results', title: 'Daily results' },
  { value: 'clicks', label: 'Clicks', title: 'Daily clicks' },
];

const SYNC_PROBLEMS = new Set(['reauth', 'error', 'disconnected']);

/** The /campaigns response nests the list a few different ways. */
function conversationList(data: any): any[] {
  let list = data?.conversations;
  if (list?.conversations && Array.isArray(list.conversations)) list = list.conversations;
  if (Array.isArray(list)) return list;
  if (list && typeof list === 'object') {
    return Object.values(list).filter((item: any) => item && typeof item === 'object' && (item.id || item.conversation));
  }
  return [];
}

/** "<Month> vs plan": this month's ad spend against the plan, its focus and next check-in. */
const PlanCheck: React.FC = () => {
  const { planState, month, spend, campaigns } = useDesk();
  const plan = planState?.plan && planState.plan.month === month ? planState.plan : null;
  if (!plan) return null;
  const next = plan.plan.checkpoints.map(c => c).sort((a, b) => a.date.localeCompare(b.date)).find(c => new Date(`${c.date}T23:59:59`) >= new Date());
  const live = campaigns.filter(c => c.status === 'live').length;
  const pct = spend.planned ? Math.min(100, (spend.spent / spend.planned) * 100) : 0;
  return (
    <section className="grid gap-6 rounded-panel bg-navy p-6 text-on-navy md:grid-cols-[1.2fr_1fr] md:p-7">
      <div className="flex min-w-0 flex-col gap-2">
        <div className="text-label uppercase text-on-navy-brand">{monthLabel(month, false)} vs plan</div>
        <div className="font-display text-[24px] font-extrabold leading-tight tracking-[-0.02em] md:text-[28px]">{plan.plan.focus[0]?.objective || plan.plan.summary.value}</div>
        <div className="text-small text-on-navy-2">{live} live campaign{live === 1 ? '' : 's'} from the plan{next ? ` · next check-in ${shortDate(next.date)}: ${next.check}` : ''}</div>
        <PlanVerdictLine plan={plan} className="text-on-navy" />
      </div>
      <div className="flex flex-col justify-center gap-2">
        <div className="flex justify-between gap-3 text-sm"><span>Spend</span><span className="font-money">{spend.known ? money(spend.spent, spend.currency) : '—'} / {money(spend.planned, spend.currency)}</span></div>
        <div className="h-2 overflow-hidden rounded-full bg-navy-3"><div className="h-full bg-on-navy" style={{ width: `${pct}%` }} /></div>
        <Link to={DESK.plan(month)} className="pt-1 text-small font-semibold text-on-navy-brand underline underline-offset-2">Open the {monthLabel(month, false)} plan →</Link>
      </div>
    </section>
  );
};

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, currentOrganization } = useAuth();
  const { theme } = useTheme();
  const capabilities = useAnalyticsCapabilities();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedParam = searchParams.get('platform');
  const selected: AdPlatform | null = PLATFORM_ORDER.includes(selectedParam as AdPlatform)
    ? (selectedParam as AdPlatform)
    : null;
  const platformParam = selected ?? 'all';

  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [chartMetric, setChartMetric] = useState<ChartMetric>('spend');
  const [campaignSort, setCampaignSort] = useState<CampaignSort>('spend');

  const [dashboard, setDashboard] = useState<DashboardMetrics | null>(null);
  const [chart, setChart] = useState<ChartData | null>(null);
  const [chartError, setChartError] = useState(false);
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [sync, setSync] = useState<PlatformSyncStatus[]>([]);
  const [leadSummary, setLeadSummary] = useState<LeadsSummary | null>(null);
  const [latestLeads, setLatestLeads] = useState<Lead[]>([]);
  const [conversations, setConversations] = useState({ loaded: false, any: false, anyActive: false });

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgVersion, setOrgVersion] = useState(0);

  // Reload everything when the active organization changes
  useEffect(() => {
    const handleOrgChange = () => {
      setDashboard(null);
      setCampaigns([]);
      setHighlights([]);
      setChart(null);
      setError(null);
      setLoading(true);
      setOrgVersion((v) => v + 1);
    };
    window.addEventListener('organizationChanged', handleOrgChange);
    return () => window.removeEventListener('organizationChanged', handleOrgChange);
  }, []);

  // Admeru campaigns (drafts included) — drive the welcome modal and empty states.
  useEffect(() => {
    if (DEMO_MODE) {
      setConversations({ loaded: true, any: true, anyActive: true });
      return;
    }
    apiClient.get('/api/v1/campaigns', { params: { limit: 20, offset: 0 } })
      .then((res) => {
        const list = conversationList(res.data).map((item: any) => item.conversation || item);
        setConversations({ loaded: true, any: list.length > 0, anyActive: list.some((c: any) => c.status === 'active') });
      })
      .catch((err) => {
        console.error('Failed to load campaigns:', err);
        setConversations((c) => ({ ...c, loaded: true }));
      });
  }, [orgVersion]);

  // Leads: independent of the time range and the analytics plan (they are the org's own data).
  useEffect(() => {
    if (DEMO_MODE) return;
    Promise.all([leadsApi.summary(), leadsApi.list({ kind: 'form' }, 4, 0)])
      .then(([summary, list]) => { setLeadSummary(summary); setLatestLeads(list.leads); })
      .catch(() => setLeadSummary(null));
  }, [orgVersion]);

  useEffect(() => {
    analyticsApi.getSyncStatus()
      .then((res) => setSync(res.platforms))
      .catch((err) => console.error('Failed to load sync status:', err));
  }, [orgVersion]);

  // Dashboard numbers come from the all-platform response; a platform filter
  // reads that platform's slice, so the platform rows always have every platform.
  const loadOverview = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const [dash, hl] = await Promise.all([
        analyticsApi.getDashboard(timeRange, 'all'),
        analyticsApi.getHighlights(timeRange, platformParam).catch(() => ({ highlights: [] })),
      ]);
      setDashboard(dash);
      setHighlights(hl.highlights);
    } catch (err: any) {
      console.error('Failed to load dashboard:', err);
      setError(err?.response?.data?.detail || err.message || 'Failed to load dashboard data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [timeRange, platformParam]);

  useEffect(() => { loadOverview(); }, [loadOverview, orgVersion]);

  useEffect(() => {
    analyticsApi.getCampaignsSummary({
      time_range: timeRange,
      platform: selected ?? undefined,
      sort_by: campaignSort,
      sort_order: campaignSort === 'cost_per_result' ? 'asc' : 'desc',
      limit: 10,
    })
      .then((res) => setCampaigns(res.campaigns))
      .catch((err) => {
        console.error('Failed to load campaigns summary:', err);
        setCampaigns([]);
      });
  }, [timeRange, selected, campaignSort, orgVersion]);

  useEffect(() => {
    setChartError(false);
    analyticsApi.getChartData(chartMetric, timeRange, platformParam)
      .then(setChart)
      .catch((err) => {
        console.error('Failed to load chart:', err);
        setChartError(true);
      });
  }, [chartMetric, timeRange, platformParam, orgVersion]);

  const selectPlatform = (platform: AdPlatform | null) => {
    const next = new URLSearchParams(searchParams);
    if (platform) next.set('platform', platform);
    else next.delete('platform');
    setSearchParams(next, { replace: true });
  };

  const handleCreateCampaign = async () => {
    try {
      const response = await apiClient.post('/api/v1/campaigns', { title: 'New Campaign' });
      navigate(`/campaigns/${response.data.id}`);
    } catch (err: any) {
      console.error('Failed to create campaign:', err);
      const status = err.response?.status;
      const errorData = err.response?.data?.error || err.response?.data;
      if (status === 402) {
        const msg = errorData?.limit != null
          ? `Campaign limit reached (${errorData.current}/${errorData.limit}). Upgrade your plan to create more campaigns.`
          : errorData?.message || 'Campaign creation requires a plan upgrade.';
        toast.warning(msg, 'Limit reached');
      } else {
        navigate('/campaigns/new');
      }
    }
  };

  const openCampaign = (c: { id?: string; campaign_id?: string; conversation_id?: string | null }) => {
    const id = c.conversation_id || c.campaign_id || c.id;
    if (id) navigate(`/campaigns/${encodeURIComponent(id)}/analytics`);
  };

  const syncByPlatform = useMemo(
    () => Object.fromEntries(sync.map((s) => [s.platform, s])) as Partial<Record<AdPlatform, PlatformSyncStatus>>,
    [sync],
  );

  // Connected platforms plus any with data in the period, in a fixed order.
  const platforms = useMemo(
    () => PLATFORM_ORDER.filter((p) => syncByPlatform[p] || dashboard?.by_platform?.[p]),
    [syncByPlatform, dashboard],
  );

  const view = selected ? dashboard?.by_platform?.[selected] ?? null : dashboard?.summary ?? null;
  const viewTrends = selected ? dashboard?.by_platform?.[selected]?.trends : dashboard?.trends;
  const syncProblems = sync.filter((s) => SYNC_PROBLEMS.has(s.state));
  const comparedTo = `previous ${RANGE_DAYS[timeRange]} days`;
  const colors = PLATFORM_COLOR[theme === 'dark' ? 'dark' : 'light'];

  const showNoAnalyticsDataState = capabilities.isAvailable && dashboard !== null && dashboard.has_data === false;
  const showMetrics = capabilities.isAvailable && dashboard !== null && !showNoAnalyticsDataState;

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-lg text-ink-2">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const chartSeries = PLATFORM_ORDER.filter((p) => chart?.series?.includes(p)).map((p) => ({ key: p, label: PLATFORM_LABEL[p] ?? p, color: colors[p] ?? '#999' }));
  const chartCurrency = chart?.currency ?? null;
  const isMoneyChart = chartMetric === 'spend';
  const chartTitle = CHART_METRICS.find((m) => m.value === chartMetric)?.title ?? '';
  const chartHasValues = (chart?.data ?? []).some((d) => d.value > 0);

  const campaignsSection =
    showMetrics && campaigns.length > 0 ? (
      <CampaignsTable
        campaigns={campaigns}
        showPlatform={!selected && platforms.length > 1}
        sort={campaignSort}
        onSort={setCampaignSort}
        onOpen={openCampaign}
      />
    ) : conversations.loaded && !conversations.any ? (
      <Card variant="outlined" padding="lg" className="!rounded-2xl">
        <CapabilityCard
          icon={TrendingUp}
          title={CAPABILITY.dashboard_roi.title}
          body={CAPABILITY.dashboard_roi.body}
          primary={{ label: CAPABILITY.dashboard_roi.primary.label, onClick: handleCreateCampaign }}
          className="py-4"
        />
      </Card>
    ) : showMetrics ? (
      <Card variant="outlined" padding="lg" className="!rounded-2xl text-center">
        <p className="text-sm text-ink-2">No campaigns delivered in the last {RANGE_DAYS[timeRange]} days.</p>
      </Card>
    ) : null;

  return (
    <div className="px-5 pb-10 pt-4 md:px-12 md:pb-16">
      <div className={`max-w-[1400px] mx-auto space-y-7 transition-opacity duration-200 ${refreshing ? 'opacity-60' : 'opacity-100'}`}>
        {/* Header + range (Results board) */}
        <header className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center gap-x-2 text-label uppercase text-brand-ink">
            {currentOrganization?.name ? `${currentOrganization.name} · ` : ''}all campaigns
            {platforms.length === 1 && syncByPlatform[platforms[0]] && (
              <span className="normal-case tracking-normal text-ink-3 font-semibold">· {PLATFORM_LABEL[platforms[0]]} <SyncLine status={syncByPlatform[platforms[0]]} /></span>
            )}
          </div>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h1 className="m-0 font-display text-[34px] md:text-display-m font-extrabold leading-none tracking-[-0.035em] text-ink">Results</h1>
            <Segmented label="Time range" value={timeRange} onChange={v => setTimeRange(v as TimeRange)}
              items={(['7d', '30d', '90d'] as const).map(r => ({ id: r, label: `${RANGE_DAYS[r]} days` }))} />
          </div>
          {showMetrics && view && (
            <p className="m-0 text-[17px] text-ink-2">
              {`${view.active_campaigns} ${view.active_campaigns === 1 ? 'campaign' : 'campaigns'} delivering${selected ? ` on ${PLATFORM_LABEL[selected]}` : ''}`}
            </p>
          )}
        </header>

        <PlanCheck />

        {/* Active platform filter */}
        {selected && (
          <div className="flex items-center gap-2 text-sm text-ink-2">
            Showing
            <button
              type="button"
              onClick={() => selectPlatform(null)}
              className="inline-flex items-center gap-1.5 pl-2 pr-1.5 py-1 rounded-full bg-primary-soft text-ink font-semibold"
              aria-label={`Clear ${PLATFORM_LABEL[selected]} filter`}
            >
              <span className="w-2 h-2 rounded-[2px]" style={{ background: colors[selected] }} />
              {PLATFORM_LABEL[selected]} only
              <svg className="w-3.5 h-3.5 text-ink-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {error && (
          <Alert variant="error" title="Couldn't load the dashboard" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {syncProblems.length > 0 && (
          <Notice tone="warn" action={<button type="button" onClick={() => navigate('/settings/businesses/active/connections')} className="text-sm font-bold text-ink underline underline-offset-2">Reconnect</button>}>
            <span className="font-semibold">Some numbers may be out of date.</span>{' '}
            {syncProblems.map((s) => `${PLATFORM_LABEL[s.platform]} ${s.state === 'reauth' ? 'needs you to sign in again' : s.state === 'disconnected' ? 'is disconnected' : 'didn’t sync'}${s.last_data_date ? ` (data to ${new Date(`${s.last_data_date}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})` : ''}`).join(' · ')}.
          </Notice>
        )}

        {!capabilities.isAvailable && (
          <AnalyticsUpgradePrompt
            level={capabilities.analyticsLevel}
            requiredLevel="advanced"
            onUpgradeClick={() => navigate('/settings/subscription')}
          />
        )}

        {/* Entitled, but nothing has delivered yet */}
        {showNoAnalyticsDataState && (
          <section className="flex flex-col gap-3 rounded-panel bg-card p-6">
            <div className="text-label uppercase text-ink-3">No results yet</div>
            <h2 className="m-0 font-display text-[24px] font-extrabold leading-tight tracking-[-0.02em] text-ink">
              {conversations.anyActive ? 'Results show up about an hour after your ads start serving.' : 'Results show up after your first ad goes live.'}
            </h2>
            <p className="m-0 max-w-[62ch] text-small text-ink-2">
              {conversations.anyActive
                ? 'Your live campaigns haven’t served yet, or the latest numbers are still syncing from the ad platforms.'
                : 'Campaigns publish paused by default. Turn one on in the ad platform, and impressions, clicks and spend arrive here.'}
            </p>
            <div className="flex flex-wrap gap-2">
              <DeskButton variant="navy" onClick={() => navigate('/campaigns')}>{conversations.anyActive ? 'See campaigns' : 'Open campaigns'}</DeskButton>
              <DeskButton onClick={() => navigate('/')}>See this month</DeskButton>
            </div>
          </section>
        )}

        {showMetrics && view && (
          <>
            {/* Headline numbers */}
            <div className={`grid grid-cols-2 gap-3 md:gap-4 ${view.roas !== null ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}>
              <MetricCard
                title="Spend"
                value={view.mixed_currency ? 'Mixed' : formatMoney(view.spend, view.currency)}
                subtitle={view.mixed_currency ? 'Accounts use different currencies — see platforms below' : undefined}
                trend={viewTrends?.spend}
                goodWhen="neutral"
                comparedTo={comparedTo}
                hint={INFO.spend}
              />
              <MetricCard
                title="Results"
                value={formatCount(view.conversions)}
                subtitle={view.conversions > 0 ? resultsBreakdown(view.results) : 'No leads, purchases or sign-ups tracked'}
                trend={viewTrends?.conversions}
                goodWhen="up"
                comparedTo={comparedTo}
                hint={INFO.results}
              />
              <MetricCard
                title="Cost per result"
                value={view.mixed_currency ? 'Mixed' : formatMoney(view.cost_per_result, view.currency)}
                subtitle={view.cpc !== null ? `${formatMoney(view.cpc, view.currency)} per click` : undefined}
                trend={viewTrends?.cost_per_result}
                goodWhen="down"
                comparedTo={comparedTo}
                hint={INFO.cost_per_result}
              />
              <MetricCard
                title="Click rate"
                value={formatPct(view.ctr)}
                subtitle={`${formatCount(view.clicks)} clicks from ${formatCount(view.impressions, { compact: true })} impressions`}
                trend={viewTrends?.ctr}
                goodWhen="up"
                comparedTo={comparedTo}
                hint={INFO.click_rate}
              />
              {view.roas !== null && (
                <MetricCard
                  title="ROAS"
                  value={`${view.roas.toFixed(2)}×`}
                  subtitle={`${formatMoney(view.conversions_value, view.currency)} in tracked sales`}
                  hint={INFO.roas}
                />
              )}
            </div>

            {/* Per-day chart (results board) */}
            <section className="min-w-0 rounded-panel bg-card px-5 py-5 md:px-6">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="m-0 font-display text-[22px] font-extrabold tracking-[-0.02em] text-ink">{CHART_METRICS.find((m) => m.value === chartMetric)?.label} per day</h2>
                <div className="flex flex-wrap gap-1.5" role="group" aria-label="Chart metric">
                  {CHART_METRICS.map((m) => (
                    <button key={m.value} type="button" onClick={() => setChartMetric(m.value)} aria-pressed={chartMetric === m.value}
                      className={`min-h-[36px] rounded-full px-3.5 text-[13px] font-bold transition-colors ${chartMetric === m.value ? 'bg-navy text-on-navy' : 'bg-inset text-ink hover:bg-line'}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              {chartError ? (
                <p className="py-20 text-center text-sm text-ink-3">Couldn't load the chart. Try again in a moment.</p>
              ) : !chart ? (
                <div className="h-[260px] animate-pulse rounded bg-inset" />
              ) : isMoneyChart && chart.mixed_currency ? (
                <p className="py-20 text-center text-sm text-ink-3">Spend can't be added up across currencies. Pick a platform below to see its spend.</p>
              ) : !chartHasValues ? (
                <p className="py-20 text-center text-sm text-ink-3">No delivery in this period.</p>
              ) : (
                <DailyChart
                  data={chart.data}
                  series={chartSeries}
                  label={`${chartTitle}${selected ? ` on ${PLATFORM_LABEL[selected]}` : ' by platform'}, last ${RANGE_DAYS[timeRange]} days`}
                  formatValue={(v) => (isMoneyChart ? formatMoney(v, chartCurrency) : formatCount(v))}
                  formatTick={(v) => (isMoneyChart ? formatMoney(v, chartCurrency, { compact: true }) : formatCount(v, { compact: true }))}
                />
              )}
            </section>
          </>
        )}

        <div className={`grid items-start gap-6 ${showMetrics && view ? 'xl:grid-cols-[1.7fr_1fr]' : ''}`}>
          <div className="flex min-w-0 flex-col gap-6">
        {showMetrics && view && (
          <>
            {/* Platforms */}
            {platforms.length > 1 && (
              <div>
                <div className="flex items-baseline justify-between mb-3.5">
                  <h2 className="font-display text-[17px] font-bold text-ink">Platforms</h2>
                  <p className="text-[12px] text-ink-3">Click a platform to filter the page</p>
                </div>
                <PlatformTable
                  platforms={platforms}
                  byPlatform={dashboard?.by_platform ?? {}}
                  sync={syncByPlatform}
                  selected={selected}
                  onSelect={selectPlatform}
                />
              </div>
            )}
          </>
        )}

        {/* Campaigns */}
        {campaignsSection && (
          <div>
            <div className="flex items-center justify-between mb-3.5">
              <h2 className="font-display text-[17px] font-bold text-ink">
                Campaigns{selected ? ` on ${PLATFORM_LABEL[selected]}` : ''}
              </h2>
              <button onClick={() => navigate('/campaigns')} className="text-[13px] font-bold text-primary hover:underline">
                View all
              </button>
            </div>
            {campaignsSection}
          </div>
        )}
          </div>
          <div className="flex min-w-0 flex-col gap-6">
            {showMetrics && view && <HighlightsCard highlights={highlights} onOpenCampaign={openCampaign} />}
        {leadSummary && (leadSummary.has_paid_campaigns || leadSummary.total > 0 || leadSummary.calls > 0) && (
          <LeadsCard
            summary={leadSummary}
            latest={latestLeads}
            onOpenAll={() => navigate('/leads')}
            onOpenLead={(lead) => navigate(`/leads?lead=${lead.id}`)}
          />
        )}

          </div>
        </div>
      </div>

      <WelcomePathModal
        isNewUser={!loading && conversations.loaded && (!conversations.any || !isSeen(WELCOME_ID))}
        onNavigate={(route) => navigate(route)}
      />
    </div>
  );
};

export default DashboardPage;
